import { API_BASE_URL } from '../config';
import React, { useState, useEffect } from 'react';

const GadgetPermissionManagement = () => {
    const [users, setUsers] = useState([]);
    const [gadgetPermissions, setGadgetPermissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Form states
    const [showAssignPermissionModal, setShowAssignPermissionModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedGadget, setSelectedGadget] = useState('');
    const [selectedDashboard, setSelectedDashboard] = useState('');
    const [permissions, setPermissions] = useState({
        VIEW: false,
        CREATE: false,
        UPDATE: false,
        DELETE: false,
        EXPORT: false
    });

    const availableGadgets = {
        'Admin': [
            'User Management',
            'Role Management',
            'User Role Management',
            'Gadget Permission Management',
            'Reference Data Management',
            'Reports',
            'Clinic Logo',
            'Theme',
            'Finance Summary'
        ],
        'Doctor': [
            'Patient Management',
            'Appointment Scheduling',
            'Room Calendar'
        ],
        'Receptionist': [
            'Patient Management',
            'Appointment Scheduling',
            'Invoice Generation',
            'Room Calendar'
        ],
        'Pharmacy': [
            'Patient Management',
            'Medicine Sales',
            'Medicine Purchases',
            'Profit Analysis',
            'Medicine Reference',
            'Low Stock and Expiry',
            'Purchase History',
            'Returns and Adjustments',
            'Suppliers',
            'Daily Counter Summary'
        ],
        'Therapist': [
            'Patient Management',
            'Procedure Management',
            'Room Calendar'
        ],
        'Finance': [
            'Sales Analytics',
            'Profit Analysis',
            'Tax Reporting',
            'Invoice Generation',
            'Finance Summary'
        ],
        'HR': [
            'Staff Directory',
            'On Duty Today',
            'Leave'
        ]
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const [usersRes, permissionsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/users`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_BASE_URL}/gadgetPermissions`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            const [usersData, permissionsData] = await Promise.all([
                usersRes.json(),
                permissionsRes.json()
            ]);

            setUsers(usersData);
            setGadgetPermissions(permissionsData);
        } catch (err) {
            setError('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleAssignPermission = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Validate required fields
        if (!selectedUser || !selectedGadget || !selectedDashboard) {
            setError('Please select user, dashboard, and gadget');
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const requestBody = {
                USER_ID: selectedUser,
                GADGET_NAME: selectedGadget,
                DASHBOARD_NAME: selectedDashboard,
                PERMISSIONS: permissions
            };
            
            console.log('Making POST request to assign gadget permission:', requestBody);
            
            const response = await fetch(`${API_BASE_URL}/gadgetPermissions/assign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });
            
            console.log('Response status:', response.status);

            const data = await response.json();
            console.log('Response data:', data);

            if (response.ok) {
                setSuccess('Gadget permission assigned successfully');
                setShowAssignPermissionModal(false);
                resetForm();
                fetchData();
            } else {
                console.error('Error response:', data);
                setError(data.message || `Failed to assign permission (Status: ${response.status})`);
            }
        } catch (err) {
            console.error('Network error:', err);
            setError(`Network error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePermission = async (permissionId, newPermissions) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/gadgetPermissions/${permissionId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    PERMISSIONS: newPermissions
                })
            });

            if (response.ok) {
                setSuccess('Permission updated successfully');
                fetchData();
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to update permission');
            }
        } catch (err) {
            setError('Network error');
        } finally {
            setLoading(false);
        }
    };

    const handleRemovePermission = async (permissionId) => {
        if (!window.confirm('Are you sure you want to remove this permission?')) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/gadgetPermissions/${permissionId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                setSuccess('Permission removed successfully');
                fetchData();
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to remove permission');
            }
        } catch (err) {
            setError('Network error');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setSelectedUser(null);
        setSelectedGadget('');
        setSelectedDashboard('');
        setPermissions({
            VIEW: false,
            CREATE: false,
            UPDATE: false,
            DELETE: false,
            EXPORT: false
        });
    };

    const getUserPermissions = (userId) => {
        return gadgetPermissions.filter(gp => gp.USER_ID._id === userId && gp.IS_ACTIVE);
    };

    const handlePermissionChange = (permission, value) => {
        setPermissions(prev => ({
            ...prev,
            [permission]: value
        }));
    };

    const handleDashboardChange = (dashboard) => {
        setSelectedDashboard(dashboard);
        setSelectedGadget(''); // Reset gadget when dashboard changes
    };

    if (loading && users.length === 0) {
        return <div className="loading">Loading...</div>;
    }

    return (
        <div className="gadget-permission-management">
            <div className="header">
                <h2>Gadget Permission Management</h2>
                <button 
                    className="btn btn-primary"
                    onClick={() => setShowAssignPermissionModal(true)}
                >
                    Assign Permission
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="permissions-table">
                <table>
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Gadget</th>
                            <th>Dashboard</th>
                            <th>Permissions</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => {
                            const userPermissions = getUserPermissions(user._id);
                            return userPermissions.length > 0 ? (
                                userPermissions.map(permission => (
                                    <tr key={permission._id}>
                                        <td>
                                            <div>
                                                <strong>{permission.USER_ID.firstName} {permission.USER_ID.lastName}</strong>
                                                <br />
                                                <small>@{permission.USER_ID.username}</small>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="gadget-badge">
                                                {permission.GADGET_NAME}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="dashboard-badge">
                                                {permission.DASHBOARD_NAME}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="permissions-list">
                                                {Object.entries(permission.PERMISSIONS).map(([perm, value]) => (
                                                    <span 
                                                        key={perm} 
                                                        className={`permission-badge ${value ? 'active' : 'inactive'}`}
                                                    >
                                                        {perm}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button
                                                    className="btn btn-sm btn-secondary"
                                                    onClick={() => {
                                                        setSelectedUser(permission.USER_ID._id);
                                                        setSelectedGadget(permission.GADGET_NAME);
                                                        setSelectedDashboard(permission.DASHBOARD_NAME);
                                                        setPermissions(permission.PERMISSIONS);
                                                        setShowAssignPermissionModal(true);
                                                    }}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleRemovePermission(permission._id)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr key={user._id}>
                                    <td>
                                        <div>
                                            <strong>{user.firstName} {user.lastName}</strong>
                                            <br />
                                            <small>@{user.username}</small>
                                        </div>
                                    </td>
                                    <td colSpan="4">
                                        <span className="no-permissions">No gadget permissions assigned</span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Assign Permission Modal */}
            {showAssignPermissionModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h3>Assign Gadget Permission</h3>
                            <button 
                                className="close-btn"
                                onClick={() => {
                                    setShowAssignPermissionModal(false);
                                    resetForm();
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <form>
                            <div className="form-group">
                                <label>User:</label>
                                <select
                                    value={selectedUser || ''}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    required
                                >
                                    <option value="">Select User</option>
                                    {users.map(user => (
                                        <option key={user._id} value={user._id}>
                                            {user.firstName} {user.lastName} (@{user.username})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Dashboard:</label>
                                <select
                                    value={selectedDashboard}
                                    onChange={(e) => handleDashboardChange(e.target.value)}
                                    required
                                >
                                    <option value="">Select Dashboard</option>
                                    {Object.keys(availableGadgets).map(dashboard => (
                                        <option key={dashboard} value={dashboard}>
                                            {dashboard}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Gadget:</label>
                                <select
                                    value={selectedGadget}
                                    onChange={(e) => setSelectedGadget(e.target.value)}
                                    required
                                    disabled={!selectedDashboard}
                                >
                                    <option value="">Select Gadget</option>
                                    {selectedDashboard && availableGadgets[selectedDashboard].map(gadget => (
                                        <option key={gadget} value={gadget}>
                                            {gadget}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Permissions:</label>
                                <div className="permissions-checkboxes">
                                    {Object.keys(permissions).map(permission => (
                                        <label key={permission} className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={permissions[permission]}
                                                onChange={(e) => handlePermissionChange(permission, e.target.checked)}
                                            />
                                            {permission}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" onClick={() => {
                                    setShowAssignPermissionModal(false);
                                    resetForm();
                                }}>
                                    Cancel
                                </button>
                                <button type="button" onClick={handleAssignPermission} disabled={loading}>
                                    {loading ? 'Assigning...' : 'Assign Permission'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style jsx>{`
                .gadget-permission-management {
                    padding: 20px;
                }

                .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }

                .permissions-table {
                    background: white;
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                }

                th, td {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #eee;
                }

                th {
                    background: #f8f9fa;
                    font-weight: 600;
                }

                .gadget-badge {
                    background: #007bff;
                    color: white;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                }

                .dashboard-badge {
                    background: #28a745;
                    color: white;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                }

                .permissions-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 5px;
                }

                .permission-badge {
                    padding: 2px 6px;
                    border-radius: 3px;
                    font-size: 11px;
                    font-weight: 500;
                }

                .permission-badge.active {
                    background: #d4edda;
                    color: #155724;
                }

                .permission-badge.inactive {
                    background: #f8d7da;
                    color: #721c24;
                }

                .action-buttons {
                    display: flex;
                    gap: 5px;
                }

                .no-permissions {
                    color: #6c757d;
                    font-style: italic;
                }

                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }

                .modal {
                    background: white;
                    border-radius: 8px;
                    padding: 0;
                    width: 90%;
                    max-width: 500px;
                    max-height: 90vh;
                    overflow-y: auto;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px;
                    border-bottom: 1px solid #eee;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 24px;
                    cursor: pointer;
                    padding: 0;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .form-group {
                    margin-bottom: 15px;
                    padding: 0 20px;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 5px;
                    font-weight: 500;
                }

                .form-group select,
                .form-group input {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 14px;
                }

                .permissions-checkboxes {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
                    gap: 10px;
                }

                .checkbox-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: normal;
                    cursor: pointer;
                }

                .checkbox-label input[type="checkbox"] {
                    width: auto;
                    margin: 0;
                }

                .modal-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 10px;
                    padding: 20px;
                    border-top: 1px solid #eee;
                }

                .modal-actions button {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 14px;
                }

                .modal-actions button[type="button"] {
                    background: #6c757d;
                    color: white;
                }

                .modal-actions button[type="submit"] {
                    background: #007bff;
                    color: white;
                }

                .modal-actions button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .error-message {
                    background: #f8d7da;
                    color: #721c24;
                    padding: 10px;
                    border-radius: 4px;
                    margin-bottom: 20px;
                    border: 1px solid #f5c6cb;
                }

                .success-message {
                    background: #d4edda;
                    color: #155724;
                    padding: 10px;
                    border-radius: 4px;
                    margin-bottom: 20px;
                    border: 1px solid #c3e6cb;
                }

                .btn {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 14px;
                    text-decoration: none;
                    display: inline-block;
                }

                .btn-primary {
                    background: #007bff;
                    color: white;
                }

                .btn-secondary {
                    background: #6c757d;
                    color: white;
                }

                .btn-danger {
                    background: #dc3545;
                    color: white;
                }

                .btn-sm {
                    padding: 4px 8px;
                    font-size: 12px;
                }

                .loading {
                    text-align: center;
                    padding: 40px;
                    font-size: 18px;
                }
            `}</style>
        </div>
    );
};

export default GadgetPermissionManagement;
